import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ServiceCardData } from '../../core/services/cloud.service';

@Component({
  selector: 'app-service-card',
  template: `
    <div
      class="relative cursor-pointer rounded-xl border bg-white p-4 shadow-sm transition-all hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-1"
      [class.border-violet-300]="active"
      [class.bg-violet-50]="active"
      [class.border-slate-200]="!active"
      (click)="navigate()"
      (keydown.enter)="onKeyActivate($event)"
      (keydown.space)="onKeyActivate($event)"
      tabindex="0"
      role="button"
      [attr.aria-pressed]="active"
      [attr.aria-label]="'View ' + card.name + ' details'"
      [attr.aria-busy]="loading || null"
    >
      <!-- Header row: icon + name | status badge + gear -->
      <div class="mb-3 flex items-start justify-between gap-2">
        <div class="flex items-center gap-2">
          <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
            <lucide-icon [name]="card.icon" [size]="16" class="text-slate-600" [strokeWidth]="1.75"></lucide-icon>
          </div>
          <span class="text-sm font-semibold text-slate-900">{{ card.name }}</span>
          <span *ngIf="card.scope === 'global'" class="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700" title="Global / account-scoped service">Global</span>
        </div>
        <div class="flex flex-shrink-0 items-center gap-1">
          <app-status-badge [status]="card.status"></app-status-badge>
          <button
            type="button"
            (click)="onRefreshClick($event)"
            (keydown.enter)="$event.stopPropagation()"
            (keydown.space)="$event.stopPropagation()"
            [disabled]="refreshing"
            class="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:cursor-not-allowed disabled:opacity-60"
            [attr.aria-label]="'Refresh ' + card.name + ' data'"
            title="Refresh"
          >
            <lucide-icon name="refresh-cw" [size]="12" [strokeWidth]="2" [class.animate-spin]="refreshing"></lucide-icon>
          </button>
          <a
            [href]="detailHref"
            target="_blank"
            rel="noopener noreferrer"
            (click)="$event.stopPropagation()"
            (keydown.enter)="$event.stopPropagation()"
            (keydown.space)="$event.stopPropagation()"
            class="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
            [attr.aria-label]="'Open ' + card.name + ' in new tab'"
            title="Open in new tab"
          >
            <lucide-icon name="external-link" [size]="12" [strokeWidth]="2"></lucide-icon>
          </a>
          <button
            type="button"
            (click)="onSettingsClick($event)"
            class="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
            [attr.aria-label]="'Configure ' + card.name + ' alerts'"
            title="Configure alerts & ticketing"
          >
            <lucide-icon name="settings" [size]="13" [strokeWidth]="2"></lucide-icon>
          </button>
        </div>
      </div>

      <!-- Metrics grid -->
      <div class="grid grid-cols-2 gap-2">
        <div *ngFor="let m of card.metrics" class="rounded-md bg-slate-50 px-2.5 py-1.5">
          <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">{{ m.label }}</p>
          <p class="mt-0.5 text-sm font-semibold text-slate-900">{{ m.value }}</p>
        </div>
      </div>

      <!-- Config summary -->
      <div class="mt-3 flex items-center justify-between gap-2">
        <p class="flex items-center gap-1 text-xs"
           [class.text-slate-400]="!configSummary"
           [class.text-slate-500]="!!configSummary">
          <lucide-icon
            name="bell"
            [size]="11"
            [strokeWidth]="2"
            [class.text-slate-300]="!configSummary"
            [class.text-violet-500]="!!configSummary"
          ></lucide-icon>
          {{ configSummary || 'No routing configured' }}
        </p>
        <p class="text-xs font-medium text-violet-600">View details →</p>
      </div>

      <!-- Loading overlay with spinner -->
      <div
        *ngIf="loading"
        class="absolute inset-0 flex items-center justify-center rounded-xl bg-white/70"
        aria-hidden="true"
      >
        <svg class="h-5 w-5 animate-spin text-violet-500" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
        </svg>
      </div>

      <!-- Active ring -->
      <div *ngIf="active && !loading" class="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-violet-500 ring-offset-1"></div>
    </div>
  `,
})
export class ServiceCardComponent {
  @Input() card!: ServiceCardData;
  @Input() active = false;
  @Input() loading = false;
  @Input() refreshing = false;
  @Input() configSummary = '';
  /** Current account + region scope — carried into the detail route so it opens already scoped. */
  @Input() account = '';
  @Input() region = '';
  @Output() settingsClick = new EventEmitter<void>();
  @Output() refresh = new EventEmitter<void>();

  constructor(private readonly router: Router) {}

  /** Query params that propagate the current account/region scope to the detail page. */
  private scopeParams(): Record<string, string> {
    const p: Record<string, string> = {};
    if (this.account) p['account'] = this.account;
    if (this.region)  p['region']  = this.region;
    return p;
  }

  /** Detail route with the current scope as query params (for the open-in-new-tab link). */
  get detailHref(): string {
    const qs = new URLSearchParams(this.scopeParams()).toString();
    return qs ? `${this.card.route}?${qs}` : this.card.route;
  }

  navigate(): void {
    if (this.loading || !this.card.route) return; // no-op for cards without a detail page (e.g. Azure v1)
    void this.router.navigate([this.card.route], { queryParams: this.scopeParams() });
  }

  /** Enter/Space activate the card like a button (and prevent the page from scrolling on Space). */
  onKeyActivate(event: Event): void {
    event.preventDefault();
    this.navigate();
  }

  onSettingsClick(event: Event): void {
    event.stopPropagation();
    this.settingsClick.emit();
  }

  onRefreshClick(event: Event): void {
    event.stopPropagation();
    if (this.refreshing) return; // guard against rapid double-clicks while in flight
    this.refresh.emit();
  }
}
